import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Form, Button, Spin, message, Card, Typography, Checkbox, Alert, Space } from 'antd'; // Added Space
import familyHistoryDiagnoses from '../utils/familyHistoryDiagnoses';

const { Title } = Typography;

// Removed createCheckboxOptions helper as we will map directly

const StructuredFamilyHistoryForm = ({ patientId, onSaveSuccess, onCancel }) => {
    const [form] = Form.useForm();
    const [loading, setLoading] = useState(false);
    const [fetching, setFetching] = useState(false);
    const [fetchError, setFetchError] = useState(null);

    const fetchHistory = useCallback(async () => {
        if (!patientId) {
            console.warn("StructuredFamilyHistoryForm: patientId is missing.");
            setFetchError("Cannot load history: Patient ID is missing.");
            return;
        }
        setFetching(true);
        setFetchError(null);
        console.log(`StructuredFamilyHistoryForm: Fetching history for patient ID: ${patientId}`);
        try {
            const response = await axios.get(`/patients/${patientId}`);
            const patientData = response?.data?.data?.patient;
            if (!patientData) {
                console.error("Error fetching family history: Invalid data structure received", response.data);
                throw new Error("Received invalid patient data structure from server.");
            }

            const currentHistory = patientData.structuredFamilyHistory || { conditions: [] };
            console.log("StructuredFamilyHistoryForm: Fetched structured family history:", currentHistory);

            form.setFieldsValue({
                conditions: currentHistory.conditions || [],
            });

        } catch (error) {
            let detailedError = "Failed to load existing family history. Please try again.";
            console.error("StructuredFamilyHistoryForm: Error fetching family history:", error);
            if (error.response) {
                console.error("Error Response Data:", error.response.data);
                console.error("Error Response Status:", error.response.status);
                detailedError = `Server Error ${error.response.status}: ${error.response.data?.message || 'Could not load history'}.`;
            } else if (error.request) {
                console.error("Error Request:", error.request);
                detailedError = "No response from server while fetching history. Check network or backend status.";
            } else {
                console.error('Error Message:', error.message);
                detailedError = `Error: ${error.message || 'An unknown error occurred'}`;
            }
            setFetchError(detailedError);
            message.error("Failed to load family history.");
        } finally {
            setFetching(false);
        }
    }, [patientId, form]);

    useEffect(() => {
        fetchHistory();
    }, [fetchHistory]);

    const onFinish = async (values) => {
        setLoading(true);
        console.log(`StructuredFamilyHistoryForm: Saving history for patient ID: ${patientId}`);
        try {
            let finalConditions = values.conditions || [];
            if (finalConditions.includes('Không có vấn đề trong gia đình') && finalConditions.length > 1) {
                finalConditions = finalConditions.filter(condition => condition !== 'Không có vấn đề trong gia đình');
            }

            const payload = {
                structuredFamilyHistory: {
                    conditions: finalConditions,
                },
            };
            console.log("StructuredFamilyHistoryForm: Saving payload:", payload);

            await axios.patch(`/patients/${patientId}`, payload);

            message.success('Family history updated successfully!');
            if (onSaveSuccess) {
                onSaveSuccess();
            }
        } catch (error) {
            console.error("StructuredFamilyHistoryForm: Error saving family history:", error);
             let detailedError = "Failed to update family history. Please try again.";
             if (error.response) {
                console.error("Error Response Data:", error.response.data);
                console.error("Error Response Status:", error.response.status);
                detailedError = `Server Error ${error.response.status}: ${error.response.data?.message || 'Could not save history'}.`;
            } else if (error.request) {
                console.error("Error Request:", error.request);
                detailedError = "No response from server while saving history. Check network or backend status.";
            } else {
                console.error('Error Message:', error.message);
                detailedError = `Error: ${error.message || 'An unknown error occurred'}`;
            }
            message.error(detailedError);
        } finally {
            setLoading(false);
        }
    };

    // Removed familyHistoryOptions constant

    return (
        <Spin spinning={fetching || loading}>
            {/* Removed outer Card as the Modal provides the container */}
            {fetchError && <Alert message={fetchError} type="error" showIcon closable style={{ marginBottom: 16 }} />}
            <Form
                form={form}
                layout="vertical"
                onFinish={onFinish}
                initialValues={{ conditions: [] }}
            >
                <Form.Item
                    name="conditions"
                    label={<Title level={5} style={{marginBottom: '10px'}}>Select applicable family history conditions:</Title>} // Use Title for label
                >
                    {/* FIX: Map diagnoses to individual Checkbox components inside Checkbox.Group */}
                    <Checkbox.Group style={{ width: '100%' }}>
                        <Space direction="vertical"> {/* Use Space for vertical layout */}
                            {familyHistoryDiagnoses.map(diagnosis => (
                                <Checkbox key={diagnosis} value={diagnosis}>
                                    {diagnosis}
                                </Checkbox>
                            ))}
                        </Space>
                    </Checkbox.Group>
                </Form.Item>

                <Form.Item style={{ textAlign: 'right', marginTop: 24, marginBottom: 0 }}> {/* Added margin and alignment */}
                    {onCancel && <Button onClick={onCancel} style={{ marginRight: 8 }}>Cancel</Button>}
                    <Button type="primary" htmlType="submit" loading={loading}>
                        Update Checklist
                    </Button>
                </Form.Item>
            </Form>
        </Spin>
    );
};

export default StructuredFamilyHistoryForm;
